import { PictureMetadata } from 'src/api/learningSessions/types';

export type { PictureMetadata };

export const DEFAULT_PICTURE_METADATA: PictureMetadata = {
  fontSize: 1,
  textAlign: 'left',
  textPosition: 'bottom',
  imageX: 50,
  imageY: 50,
};

export const BOLD_MARKER = '**';
export const ITALIC_MARKER = '*';

const LEGACY_PREFIX_RE = /^\[\[(\{.*?\})\]\]\n/s;

// Inline tags allowed in learner content (mems, nuggets). Obsidian-style HTML
// such as `<u>underline</u>` and `<ruby>漢字<rt>かんじ</rt></ruby>`. Attributes
// are deliberately not allowed, so no event handlers or URLs can reach the
// renderer. Native clients should honour the same allow-list.
const ALLOWED_INLINE_TAG_RE =
  /^<\/?(?:u|mark|em|strong|del|s|ins|sub|sup|br|ruby|rb|rt|rp)\s*\/?>/i;

const HIGHLIGHT_RE = /==([^=\n]+)==/g;

// Splits on code spans and fences, keeping them in the result (odd indexes).
const CODE_SEGMENT_RE = /(```[\s\S]*?```|`[^`\n]*`)/;

const escapeDisallowedHtml = (text: string): string =>
  text.replace(/</g, (match, offset: number) =>
    ALLOWED_INLINE_TAG_RE.test(text.slice(offset)) ? match : '&lt;',
  );

const toHighlightMarkup = (text: string): string => text.replace(HIGHLIGHT_RE, '<mark>$1</mark>');

/**
 * Turns stored mem/nugget text into markdown that is safe to render: strips the
 * legacy mem metadata prefix, escapes HTML outside the inline allow list and
 * rewrites Obsidian's `==highlight==` syntax. Code spans and fences are left
 * untouched.
 *
 * Shared dialect for web and later native clients:
 * - CommonMark + GFM (bold, italic, lists, blockquotes, tables, strikethrough)
 * - `==highlight==` → `<mark>`
 * - Allowed inline HTML: u, mark, em, strong, del, s, ins, sub, sup, br,
 *   ruby, rb, rt, rp
 */
export function toMemMarkdown(raw: string | null | undefined): string {
  if (!raw) return '';

  return raw
    .replace(LEGACY_PREFIX_RE, '')
    .split(CODE_SEGMENT_RE)
    .map((segment, index) =>
      index % 2 === 1 ? segment : toHighlightMarkup(escapeDisallowedHtml(segment)),
    )
    .join('');
}

const LEADING_ASTERISKS_RE = /^\*+/;
const TRAILING_ASTERISKS_RE = /\*+$/;
const EMPHASIS_RUN_PATTERN = '(\\*{1,3})([^*\\n]+?)\\1';

/** Number of asterisks wrapping the text: 1 italic, 2 bold, 3 bold italic. */
const emphasisLevel = (text: string): number => {
  const leading = LEADING_ASTERISKS_RE.exec(text)?.[0].length ?? 0;
  const trailing = TRAILING_ASTERISKS_RE.exec(text)?.[0].length ?? 0;
  const level = Math.min(leading, trailing, 3);
  return text.length > level * 2 ? level : 0;
};

const levelHasMarker = (level: number, marker: string): boolean =>
  marker === BOLD_MARKER ? level >= 2 : level === 1 || level === 3;

export function toggleMarkupWrap(text: string, marker: string): string {
  if (levelHasMarker(emphasisLevel(text), marker)) {
    return text.slice(marker.length, text.length - marker.length);
  }
  return `${marker}${text}${marker}`;
}

/** Whether the text contains any emphasis of the given marker. */
export function hasMarkupFormat(text: string, marker: string): boolean {
  const re = new RegExp(EMPHASIS_RUN_PATTERN, 'g');
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (levelHasMarker(match[1].length, marker)) return true;
  }

  return false;
}

export function applyMarkupToSelection(
  text: string,
  marker: string,
  selectionStart: number,
  selectionEnd: number,
): { text: string; newSelectionStart: number; newSelectionEnd: number } {
  if (selectionStart === selectionEnd) {
    return {
      text: toggleMarkupWrap(text, marker),
      newSelectionStart: selectionStart,
      newSelectionEnd: selectionEnd,
    };
  }

  const before = text.slice(0, selectionStart);
  const selected = text.slice(selectionStart, selectionEnd);
  const after = text.slice(selectionEnd);
  const isRemoving = levelHasMarker(emphasisLevel(selected), marker);
  const innerLength = isRemoving ? selected.length - marker.length * 2 : selected.length;
  const toggled = isRemoving
    ? selected.slice(marker.length, selected.length - marker.length)
    : `${marker}${selected}${marker}`;
  const innerStart = isRemoving ? selectionStart : selectionStart + marker.length;

  return {
    text: before + toggled + after,
    newSelectionStart: innerStart,
    newSelectionEnd: innerStart + innerLength,
  };
}
